/**
 * 공지 팝업 창을 여는 일 — 창을 <b>그리는</b> 쪽({@code NoticePopupWindow})과 나눠 두었다.
 *
 * <p>한 파일에 함께 두면 화면 조각과 함수가 섞여 개발 중 새로고침이 통째로 다시 돈다.
 * 여는 쪽을 부르는 것은 홈이고, 그리는 쪽을 부르는 것은 라우터라 애초에 손님도 다르다.
 */

/** 창 크기. 공지 한 건이 사진 한두 장까지 스크롤 없이 들어가는 크기다. */
const POPUP_WIDTH = 560;

const POPUP_HEIGHT = 720;

/** 팝업 창의 주소. <b>글 id 가 들어가지 않는다</b> — 이유는 아래 {@link openNoticeWindow} 에 적었다. */
const POPUP_PATH = '/notice-popup';

/** 창 이름 앞머리. 뒤에 글 id 가 붙고, 열린 창은 그것으로 «내가 어느 공지인지» 를 안다. */
const WINDOW_NAME_PREFIX = 'bomi-notice-';

/**
 * 이 글의 팝업 창을 연다 — <b>막혔으면 null</b> 이다.
 *
 * <h2>주소에 글 id 를 싣지 않는다 (사용자 지적 2026-09-10)</h2>
 * 팝업 창에도 주소 줄은 <b>반드시 보인다</b> — 브라우저가 강제하는 것이라 끌 수 없다(`location=no`
 * 는 무시된다). 그러면 남은 선택은 <b>거기에 무엇이 적히느냐</b> 뿐인데, 주소에 id 를 넣으면
 * 사람이 읽을 일 없는 36자리 UUID 가 공지 위에 늘 떠 있게 된다.
 *
 * <p>그래서 id 는 <b>창 이름</b>으로 건넨다. `window.open(주소, 이름)` 의 이름이 열린 창의
 * {@code window.name} 이 되고(표준 동작), 그 값은 새로고침에도 남는다. 주소 줄에는
 * {@code …/notice-popup} 한 줄만 남는다.
 *
 * <p>이름은 원래도 필요했다: 같은 이름이면 브라우저가 <b>새로 쌓지 않고 그 창을 다시 쓴다</b> —
 * 홈을 새로고침할 때마다 같은 공지의 창이 하나씩 늘어나는 것을 이것이 막는다.
 *
 * @param index 몇 번째 창인가. 공지가 여럿이면 조금씩 어긋나게 놓는다 — 정확히 겹쳐 놓으면
 *              맨 위 하나만 보이고 뒤의 것은 열린 줄도 모른다
 */
export function openNoticeWindow(postId: string, index: number): Window | null {
  const left = window.screenX + 60 + index * 28;
  const top = window.screenY + 60 + index * 28;
  const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`;

  try {
    return window.open(POPUP_PATH, `${WINDOW_NAME_PREFIX}${postId}`, features);
  } catch {
    // 차단은 대개 null 로 돌아오지만, 막는 방식은 브라우저·확장 프로그램마다 다르다.
    // 어느 쪽이든 「못 열었다」 는 같은 답이라야 부르는 쪽이 대신 세울 수 있다.
    return null;
  }
}

/**
 * 세울 공지가 여럿일 때 쓰는 <b>목록 창</b>의 이름 (사용자 지시 2026-11-02). 앞머리
 * {@link WINDOW_NAME_PREFIX} 로 시작하지 않아야 한다 — 그러면 «list» 라는 글 id 로 읽힌다.
 */
const LIST_WINDOW_NAME = 'bomi-notices';

/**
 * 공지 여럿을 <b>창 하나</b>에 목록으로 연다 — 막혔으면 null. 간만에 들어온 사람 앞에 창이 겹겹이
 * 뜨던 것을 이것이 대신한다. 어느 글을 세울지는 창이 스스로 고른다(서버 목록 − 안 보기 기록).
 */
export function openNoticeListWindow(): Window | null {
  const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${window.screenX + 60},top=${window.screenY + 60},resizable=yes,scrollbars=yes`;

  try {
    return window.open(POPUP_PATH, LIST_WINDOW_NAME, features);
  } catch {
    return null;
  }
}

/** 이 창이 {@link openNoticeListWindow} 가 연 목록 창인가. */
export function isNoticeListWindow(): boolean {
  return window.name === LIST_WINDOW_NAME;
}

/**
 * 열린 창이 <b>자기가 어느 공지인지</b> 알아내는 길. 이름에 앞머리가 없으면 우리가 연 창이 아니다
 * (사람이 주소를 직접 친 경우) — 그때는 세울 것이 없다.
 */
export function noticeIdOfThisWindow(): string | null {
  const name = window.name ?? '';
  return name.startsWith(WINDOW_NAME_PREFIX) ? name.slice(WINDOW_NAME_PREFIX.length) : null;
}
